import dayjs from "dayjs";
import { code128B } from "../printing/barcodes/code128";
import generatePDF, { PDFObjectType, PDFPage, helveticaWidthTable } from "./pdf-generation";
import { SingleBitPerPixelBitmap } from "../printing/barcodes/pdf417";


// we need to be able to produce a printable and downloadable pdf that contains:
//      - a Code 128 barcode, which encodes an FNSKU number (code 128 is just what amazon expects)
//      - the textual form of the FNSKU number
//      - the product name to which the FNSKU refers
//      - the product condition
//      - (if and only if the product has an expiration date) the expiration date of the product
export default function base64FNSKULabelPdf(
  // these are in inches
  size: {
    width: number,
    height: number
  },

  fnsku: string,
  productName: string,
  productCondition: string,
  copies: number,
  expirationDate?: string
): { data: string, correctedSize: { width: number, height: number } } {  
  // default 3.5x1.125" label
  if (!size || !size.width || !size.height) {
    size = { width: 3.5, height: 1.25 };
  }

  // the longer dimension is automatically treated as the 'width', so we ensure we have 
  // a good amount of space for the text.
  let pdfWidth = size.width*72;
  let pdfHeight = size.height*72;
  let outputSize = size;
  if (size.width < size.height) {
    outputSize = { width: size.height, height: size.width };
    pdfWidth = size.height*72;
    pdfHeight = size.width*72;
  }

  const objects = fnskuLabelObjects(pdfWidth, fnsku, productName, productCondition, expirationDate);

  const pages = [];
  for (let i = 0; i < copies; i++) {
    pages.push({ objects });
  }

  const pdf = generatePDF(pdfWidth, pdfHeight, pages);

  return { data: window.btoa(pdf), correctedSize: outputSize };
}

export interface FNSKULabelSpecWithQuantity extends FNSKULabelSpec {
  quantity: number;
}

// same as above but lets you encode multiple different FNSKU labels into a single PDF
export function base64FNSKULabelPDFMulti(
  size: {
    widthInches: number,
    heightInches: number
  },
  labels: FNSKULabelSpecWithQuantity[],
): string {
  // default 3.5x1.125" label
  if (!size || !size.widthInches || !size.heightInches) {
    size = { widthInches: 3.5, heightInches: 1.25 };
  }

  // the longer dimension is automatically treated as the 'width', so we ensure we have 
  // a good amount of space for the text.
  let pdfWidth = size.widthInches*72;
  let pdfHeight = size.heightInches*72;
  let outputSize = size;
  if (size.widthInches < size.heightInches) {
    outputSize = { widthInches: size.heightInches, heightInches: size.widthInches };
    pdfWidth = size.heightInches*72;
    pdfHeight = size.widthInches*72;
  }

  const pages: PDFPage[] = [];
  for (const label of labels) {
    const objects = fnskuLabelObjects(pdfWidth, label.fnsku, label.productName, label.productCondition, label.expirationDate);
    for (let i = 0; i < label.quantity; i++) {
      pages.push({ objects });
    }
  }

  const pdf = generatePDF(pdfWidth, pdfHeight, pages);
  return window.btoa(pdf);
}

const fnskuLabelObjects = (
  labelWidth: number, // pdf units
  fnsku: string,
  productName: string,
  productCondition: string,
  expirationDate?: string,
  offsets: { x: number, y: number } = { x: 0, y : 0 }
) => {

  // generate the barcode bitmap:
  const bitmap: SingleBitPerPixelBitmap = code128B(fnsku);

  // calculate name word-wrapping.
  // this kinda sucks, but there isn't a way to do dynamic word-wrapping in the pdf spec at all, so we do it ourselves!
  // it would be trivial with a monospace font, but we use helvetica for legibility reasons right now and can't switch 
  // to Courier, (because it's poor legibility) and that's the only supported monospace font in the pdf spec by default. 
  // we may at some point embed Consolas to fix this. (MWS recommends Consolas, but no one actually does it, including
  // sellercentral!)
  let name = "";
  {
    let lines = 1;
    let currentRowLength = 0;
    // the first regex removes non-ascii characters (which breaks btoa below if present, 'B08Y9B76VJ' asin
    // has this issue), and the second splits on whitespace.
    const splittedWords = productName.replace(/[^\x20-\x7E]/g, "").split(/(\S+)/);
    start: for (const word of splittedWords) {
      let wordLength = 0;
      for (let i = 0; i < word.length; i++) {
        wordLength += helveticaWidthTable[word[i]]/2;
      }

      if (lines === 3 && (currentRowLength + wordLength) > (labelWidth-18-8)/2) { // the -8 is compensating for the with of the ellipsis itself.
        // we're on line 3, and we would be more than halfway across it. we have to truncate here :(
        name += "...";
        break start;
    
      } else if ((currentRowLength + wordLength) > (labelWidth - 18)) {
        // we can't fit on the current line, make a new line
        lines++;
        name += `\n${word}`;
        currentRowLength = wordLength;

      } else {
        name += `${word}`;
        currentRowLength += wordLength;
      }
    }
  }

  const objects = [ 
    {
      type: PDFObjectType.singleBitPerPixelImage,
      bitmap: bitmap,
      left: offsets.x + 14, // this should probably be 18, but practically 14 looks better due to the quiet zone in the barcode
      bottom: offsets.y + 43,
      width: labelWidth-18, // this should be 36, but because 18 looks better due to the quiet zone in the barcode
      height: 20,
    },
    {
      type: PDFObjectType.text,
      text: `${fnsku}, ${productCondition}`,
      fontSize: 8,
      left: offsets.x + 18,
      bottom: offsets.y + 33,
    },
    {
      type: PDFObjectType.text,
      text: name,
      fontSize: 8,
      left: offsets.x + 18,
      bottom: offsets.y + 25,
    }
  ];

  if (expirationDate) {
    // see 'expiration dates' section: https://sellercentral.amazon.com/help/hub/reference/external/G200141500#mnd_2jc_jcb-11
    // NOTE!@! this contradicts the old MWS documentation, dated 2016, where expiration dates on 2D barcode labels should be in format YYMMDD!!!
    // it's a different kind of label, so probably ok, but it's weird that they have two different standards, depending on the label type.
    const date = dayjs.utc(expirationDate).format("MM/DD/YYYY");
    const expirationDateText = `EXP: ${date}`;
    let textLength = 0;
    for (let i = 0; i < expirationDateText.length; i++) {
      textLength += helveticaWidthTable[expirationDateText[i]]/2; // divide-by-two because the width table is 16px
    }
    objects.push({
      type: PDFObjectType.text,
      text: expirationDateText,
      fontSize: 8,
      left: offsets.x + (labelWidth - textLength - 18), // -18 to hit 0.25" horizontal margin
      bottom: offsets.y + 9,
    });
  }

  return objects;
};

export interface FNSKULabelSpec {
  fnsku: string;
  productName: string;
  productCondition: string;
  expirationDate?: string;
}

const mmToPDFUnits = (mm: number): number => {
  return (mm * 0.0393701) * 72;
}
  
export const thirtyUpLabels = (labels: FNSKULabelSpec[]): string => {

  // /MediaBox [ 0 0 612 792 ]             % 8.5 x 11 inches, US Letter size <-- CORRECT SIZE FOR 30up
  const pdfWidth = 612;
  const pdfHeight = 792;

  // all 30up labels I've tried (sample size is 5 brands) have a top margin of 13mm, and a left margin of 5mm.
  // with a margin of error of 1mm for the vertical dimension, and 0.9mm for the horizontal. Necessarily, because
  // of how precious this is, little things like misprints of the 30up label sheet, user/printer error relating
  // to the physical placement of the sheet of paper in the printer, could cause the printed sheet to not look correct.

  // @TODO if we find that some 30up label sheets vary drastically from the norm (has not happened yet)
  // we can make the following variables parameters in settings, which should be enough information to
  // print the sheet correctly, no matter the specifics.

  const numLabelsPerSheet = 30;

  // measurements from the edges of the sheet of paper to the edge of the labels themselves:
  const topMargin = mmToPDFUnits(13);
  const leftMargin = mmToPDFUnits(5);
  const labelWidth = mmToPDFUnits(63.5);
  const labelHeight = mmToPDFUnits(25.4);

  // gaps horizontally/vertically between consecutive labels:
  const labelVerticalGap = 0;
  const labelHorizontalGap = mmToPDFUnits(3.5);

  const startX = leftMargin;
  const startY = pdfHeight-labelHeight-topMargin;
  let cursorX = startX;
  let cursorY = startY;

  const pages: PDFPage[] = [ { objects: [] } ];
  for (let i = 0; i < labels.length; i++) {

    if ((i - (pages.length-1)*numLabelsPerSheet) === numLabelsPerSheet) {
      pages.push({ objects: [] });
    }

    pages[pages.length-1].objects.push(...fnskuLabelObjects(labelWidth, labels[i].fnsku, labels[i].productName, labels[i].productCondition, labels[i].expirationDate, { x: cursorX, y: cursorY }));

    cursorY -= labelHeight + labelVerticalGap;

    if ((i+1) % numLabelsPerSheet === 0) {
      // we are crossing a page boundary
      cursorX = startX;
      cursorY = startY;

    } else if ((i+1) % (numLabelsPerSheet/3) === 0) {
      // we are crossing a column boundary
      cursorX += labelWidth + labelHorizontalGap;
      cursorY = startY;
    }
  }

  const pdf = generatePDF(pdfWidth, pdfHeight, pages);
  return window.btoa(pdf);
}
